import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { storeConfig } from '@/mocks/storeConfig';
import type { StoreConfig } from '@/types';
import type { StoreColors } from '@/lib/services/store.service';
import type { ModoDeCor, Template } from '@/templates/tipos';

// Maps API color keys → CSS variable names
const CSS_VAR_MAP: Record<keyof StoreColors, string> = {
  colorPrimary: 'primary',
  colorPrimaryForeground: 'primary-foreground',
  colorPrimaryHover: 'primary-hover',
  colorAccent: 'accent',
  colorAccentForeground: 'accent-foreground',
  colorAccentHover: 'accent-hover',
  colorBackground: 'background',
  colorBackgroundSecondary: 'background-secondary',
  colorForeground: 'foreground',
  colorCard: 'card',
  colorCardForeground: 'card-foreground',
  colorBorder: 'border',
  colorMuted: 'muted',
  colorMutedForeground: 'muted-foreground',
  colorIcon: 'icon',
};

// Converts HEX (#rrggbb) to "H S% L%" — needed if DB has HEX values
function hexToHsl(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  let h = 0, s = 0
  const l = (max + min) / 2
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
      case g: h = ((b - r) / d + 2) / 6; break
      case b: h = ((r - g) / d + 4) / 6; break
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

const SAFE_COLOR_RE = /^(#[0-9A-Fa-f]{3,6}|\d{1,3} \d{1,3}% \d{1,3}%)$/

/**
 * Uma cor do painel (`#rgb`, `#rrggbb` ou `H S% L%`) no formato das variáveis
 * CSS da loja (`H S% L%`). Devolve `null` para qualquer outra coisa: o valor
 * vai para o CSS, então só passa o que é cor.
 */
export function normalizeColor(value: string): string | null {
  const trimmed = value.trim().replace(/\s+/g, ' ')
  if (!SAFE_COLOR_RE.test(trimmed)) return null
  if (!trimmed.startsWith('#')) return trimmed
  // `#abc` vira `#aabbcc`; 4 ou 5 dígitos não é cor.
  const hex = trimmed.length === 4 ? `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}` : trimmed
  return hex.length === 7 ? hexToHsl(hex) : null
}

// Increases the lightness component of an "H S% L%" string by `amount`
function lightenHsl(hsl: string, amount: number): string {
  const m = hsl.match(/^(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%$/);
  if (!m) return hsl;
  const l = Math.min(100, parseFloat(m[3]) + amount);
  return `${m[1]} ${m[2]}% ${l}%`;
}

/** As cores da marca: as únicas do painel que valem num template com superfícies próprias. */
const CORES_DA_MARCA: (keyof StoreColors)[] = [
  'colorPrimary',
  'colorPrimaryForeground',
  'colorPrimaryHover',
  'colorAccent',
  'colorAccentForeground',
  'colorAccentHover',
  'colorIcon',
];

/** Tudo o que este provider pode escrever no `<html>`, para limpar ao trocar. */
const VARIAVEIS_ESCRITAS = [
  ...Object.values(CSS_VAR_MAP),
  'ring', 'primary-glow', 'accent-glow', 'popover', 'popover-foreground',
  'card-hover', 'background-tertiary', 'input', 'secondary', 'secondary-hover', 'secondary-foreground',
];

const chaveDoModo = (storeId: string) => `hystore:loja:${storeId}:modo`;

function lerModoSalvo(storeId: string | undefined): ModoDeCor | null {
  if (!storeId) return null;
  try {
    const salvo = window.localStorage.getItem(chaveDoModo(storeId));
    return salvo === 'dark' || salvo === 'light' ? salvo : null;
  } catch {
    return null;
  }
}

function guardarModo(storeId: string | undefined, modo: ModoDeCor) {
  if (!storeId) return;
  try {
    window.localStorage.setItem(chaveDoModo(storeId), modo);
  } catch {
    // Navegação privada ou armazenamento bloqueado: o modo vale só nesta visita.
  }
}

interface ThemeContextType {
  isDark: boolean;
  toggleDarkMode: () => void;
  /** O template desenha os dois modos, então o jogador pode trocar. */
  podeTrocarModo: boolean;
  config: StoreConfig;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  storeColors?: StoreColors | null;
  /** O template da loja; sem ele, o comportamento do clássico (escuro, todas as cores). */
  template?: Pick<Template, 'id' | 'modos' | 'coresDoLojista' | 'fontes'> | null;
  /** O modo que o lojista escolheu para a vitrine abrir. */
  modoPadrao?: ModoDeCor | null;
  /** O editor do construtor manda no modo: nada é lido nem guardado no navegador. */
  modoForcado?: ModoDeCor;
  storeId?: string;
}

export function ThemeProvider({ children, storeColors, template, modoPadrao, modoForcado, storeId }: ThemeProviderProps) {
  const modos: readonly ModoDeCor[] = template?.modos?.length ? template.modos : ['dark'];
  const podeTrocarModo = modos.length > 1;
  const soMarca = template?.coresDoLojista === 'marca';

  /*
   * Ordem: o que o jogador escolheu nesta loja, depois o que o lojista
   * escolheu, depois o primeiro modo do template. Um template só escuro
   * ignora as duas escolhas.
   */
  const [escolhido, setIsDark] = useState<boolean>(() => {
    const salvo = podeTrocarModo ? lerModoSalvo(storeId) : null;
    if (salvo && modos.includes(salvo)) return salvo === 'dark';
    if (modoPadrao && modos.includes(modoPadrao)) return modoPadrao === 'dark';
    return modos[0] === 'dark';
  });
  // No editor o tema muda com a loja aberta: o modo pedido vale se o tema o desenha, senão o único que ele tem.
  const isDark = modoForcado ? (modos.includes(modoForcado) ? modoForcado : modos[0]) === 'dark' : escolhido;

  // Apply store colors from DB to CSS variables
  useEffect(() => {
    const root = document.documentElement;
    VARIAVEIS_ESCRITAS.forEach((nome) => root.style.removeProperty(`--${nome}`));
    // No Personalizado todas as cores vêm do tema do construtor (uma folha de estilo): nada do painel entra.
    if (!storeColors || template?.coresDoLojista === 'nenhuma') return;

    // Num template com superfícies próprias, só as cores da marca entram.
    const valeACor = (key: keyof StoreColors) => !soMarca || CORES_DA_MARCA.includes(key);

    // Apply all direct mappings
    (Object.keys(CSS_VAR_MAP) as (keyof StoreColors)[]).forEach((key) => {
      if (!valeACor(key)) return;
      const value = storeColors[key];
      if (!value) return;
      const safe = normalizeColor(value);
      if (safe) root.style.setProperty(`--${CSS_VAR_MAP[key]}`, safe);
    });

    // --ring tracks --primary (used for focus rings)
    if (storeColors.colorPrimary) {
      const primary = normalizeColor(storeColors.colorPrimary);
      if (primary) {
        root.style.setProperty('--ring', primary);
        root.style.setProperty('--primary-glow', lightenHsl(primary, 10));
      }
    }

    // --accent-glow: L+11 (used in --gradient-accent and shadow-glow-accent)
    if (storeColors.colorAccent) {
      const accent = normalizeColor(storeColors.colorAccent);
      if (accent) root.style.setProperty('--accent-glow', lightenHsl(accent, 11));
    }

    if (soMarca) return;

    // --popover mirrors --card (dropdown menus, tooltips)
    if (storeColors.colorCard) {
      const card = normalizeColor(storeColors.colorCard);
      if (card) root.style.setProperty('--popover', card);
    }
    if (storeColors.colorCardForeground) {
      const cardFg = normalizeColor(storeColors.colorCardForeground);
      if (cardFg) root.style.setProperty('--popover-foreground', cardFg);
    }

    // --card-hover, --background-tertiary, --input, --secondary, --secondary-hover
    // derivados de background-secondary
    if (storeColors.colorBackgroundSecondary) {
      const bgSec = normalizeColor(storeColors.colorBackgroundSecondary);
      if (bgSec) {
        const derived = lightenHsl(bgSec, 3);
        root.style.setProperty('--card-hover', derived);
        root.style.setProperty('--background-tertiary', derived);
        root.style.setProperty('--input', derived);
        root.style.setProperty('--secondary', bgSec);
        root.style.setProperty('--secondary-hover', lightenHsl(bgSec, 5));
      }
    }

    // --secondary-foreground mirrors foreground
    if (storeColors.colorForeground) {
      const fg = normalizeColor(storeColors.colorForeground);
      if (fg) root.style.setProperty('--secondary-foreground', fg);
    }
  }, [storeColors, soMarca, template?.coresDoLojista]);

  // O template vira atributo do <html>: é nele que o CSS de cada template se pendura.
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.template = template?.id ?? 'classico';
    return () => {
      delete root.dataset.template;
    };
  }, [template?.id]);

  // As fontes do template entram por <link>, e só na loja que usa esse template.
  useEffect(() => {
    if (!template?.fontes) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = template.fontes;
    link.dataset.fontesDoTemplate = template.id;
    document.head.appendChild(link);
    return () => link.remove();
  }, [template?.fontes, template?.id]);

  // Apply dark/light mode class
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }
    root.style.colorScheme = isDark ? 'dark' : 'light';
  }, [isDark]);

  const toggleDarkMode = () => {
    if (!podeTrocarModo || modoForcado) return;
    const escuro = !isDark;
    guardarModo(storeId, escuro ? 'dark' : 'light');
    setIsDark(escuro);
  };

  return (
    <ThemeContext.Provider value={{ isDark, toggleDarkMode, podeTrocarModo, config: storeConfig }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
